import React, { useState, useEffect } from 'react';
import { Users, LogIn, Heart, HeartCrack } from 'lucide-react';
import { supabase, useAuthStore } from '../lib/supabase';
import { Link } from 'react-router-dom';
import ReactionButton from './ReactionButton';
import { 
  REACTION_TYPES, 
  ReactionType, 
  addCardReaction, 
  removeCardReaction
} from '../services/pokemonTcgApi';
import { useUserReactionsForCard, useReactionActions } from '../lib/reactionStore';
import { CommunityIconAnimated } from './icons/NavIcons';

interface SentimentCheckProps {
  cardId: string;
  cardName: string;
}

interface VoteStats {
  likes_count: number;
  dislikes_count: number;
  total_votes: number;
  sentiment_score: number;
}

export default function SentimentCheck({ cardId, cardName }: SentimentCheckProps) {
  const { isAuthenticated, user } = useAuthStore();
  const userReactions = useUserReactionsForCard(cardId);
  const reactionActions = useReactionActions();
  const [stats, setStats] = useState<VoteStats>({ 
    likes_count: 0,
    dislikes_count: 0,
    total_votes: 0,
    sentiment_score: 0
  });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user's existing reactions and stats
  useEffect(() => {
    if (isAuthenticated && user) {
      loadUserReactions();
    }
    loadStats();
  }, [isAuthenticated, user, cardId]);

  const loadUserReactions = async () => {
    if (!user) return;

    try {
      // Load reactions into the global store
      await reactionActions.loadUserReactionsForCard(user.id, cardId);
      
      // Show results if user has reactions
      if (userReactions.length > 0) {
        setShowResults(true);
      }
    } catch (err) {
      console.error('Error loading user reactions:', err);
    }
  };

  const loadStats = async () => {
    try {
      console.log('Loading stats for card:', cardId);
      
      const { data, error } = await supabase
        .from('card_votes')
        .select('liked, disliked')
        .eq('card_id', cardId);

      if (error) throw error;

      let likesCount = 0;
      let dislikesCount = 0;

      // Count true boolean values
      data?.forEach(vote => {
        if (vote.liked) likesCount++;
        if (vote.disliked) dislikesCount++;
      });

      const totalVotes = likesCount + dislikesCount;
      
      // Calculate simple sentiment score (0-1, where 1 is 100% likes)
      const sentimentScore = totalVotes > 0 ? (likesCount / totalVotes) : 0;

      console.log('Stats loaded:', { likesCount, dislikesCount, totalVotes, sentimentScore });

      setStats({
        likes_count: likesCount,
        dislikes_count: dislikesCount,
        total_votes: totalVotes,
        sentiment_score: sentimentScore
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleReactionChange = (cardId?: string, reactionType?: ReactionType, isAdding?: boolean) => {
    // Reload user reactions and stats when reaction changes
    if (user && cardId) {
      reactionActions.loadUserReactionsForCard(user.id, cardId);
    }
    loadStats();
    setShowResults(true);
  };

  const handlePrimaryReaction = async (reactionType: 'like' | 'dislike') => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const hasReaction = userReactions.includes(reactionType);
      
      // Optimistically update the global store
      reactionActions.optimisticallyUpdateReaction(cardId, reactionType, !hasReaction);

      // Use the updated addCardReaction function which handles the boolean logic directly
      const success = await addCardReaction(user.id, cardId, reactionType);
      
      if (!success) {
        throw new Error('Failed to update reaction');
      }

      // Refresh data from the server
      await reactionActions.loadUserReactionsForCard(user.id, cardId);
      loadStats();
      setShowResults(true);
    } catch (err) {
      // Revert optimistic update on error
      reactionActions.revertOptimisticUpdate(cardId, reactionType, !hasReaction);
      setError('Failed to update reaction. Please try again.');
      console.error('Error updating primary reaction:', err);
    } finally {
      setLoading(false);
    }
  };

  const getSentimentText = () => {
    if (stats.total_votes === 0) return 'No votes yet';
    
    const percentage = Math.round(stats.sentiment_score * 100);
    if (percentage >= 80) return 'Highly Liked';
    if (percentage >= 60) return 'Well Liked';
    if (percentage >= 40) return 'Mixed Reception';
    if (percentage >= 20) return 'Divisive';
    return 'Not Popular';
  };

  const getSentimentColor = () => {
    if (stats.total_votes === 0) return 'text-white/40';
    
    const percentage = Math.round(stats.sentiment_score * 100);
    if (percentage >= 80) return 'text-green-400';
    if (percentage >= 60) return 'text-green-500';
    if (percentage >= 40) return 'text-yellow-500';
    if (percentage >= 20) return 'text-orange-500';
    return 'text-red-500';
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-3 sm:p-4">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <CommunityIconAnimated size={18} className="flex-shrink-0" />
        <h3 className="text-sm sm:text-lg font-semibold text-white">Community Sentiment</h3>
      </div>

      {!isAuthenticated ? (
        <div className="text-center py-4 sm:py-6">
          <LogIn size={20} className="mx-auto mb-2 sm:mb-3 text-white/40" />
          <p className="text-white/60 mb-3 sm:mb-4 text-xs sm:text-sm">Sign in to vote</p>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-xs sm:text-sm"
          >
            <LogIn size={14} />
            Sign In
          </Link>
        </div>
      ) : (
        <>
          {/* Primary Reactions (Like/Dislike) */}
          <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
            <p className="text-xs sm:text-sm text-white/60 font-medium">How do you feel about this card?</p>
            <div className="flex gap-2 sm:gap-3">
              {/* Like Button */}
              <button
                onClick={() => handlePrimaryReaction('like')}
                disabled={loading}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all border text-xs sm:text-sm ${
                  userReactions.includes('like')
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'bg-white/5 border-white/20 text-white/60 hover:bg-white/10 hover:text-white hover:border-white/30'
                } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Heart
                  size={14} 
                  className={`sm:w-4 sm:h-4 ${userReactions.includes('like') ? 'fill-current' : ''}`} 
                />
                <span>Like</span>
                {stats.likes_count > 0 && (
                  <span className="bg-black/20 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs">
                    {stats.likes_count}
                  </span>
                )}
              </button>

              {/* Dislike Button */}
              <button
                onClick={() => handlePrimaryReaction('dislike')}
                disabled={loading}
                className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all border text-xs sm:text-sm ${
                  userReactions.includes('dislike')
                    ? 'bg-gray-500/20 border-gray-500/50 text-gray-400'
                    : 'bg-white/5 border-white/20 text-white/60 hover:bg-white/10 hover:text-white hover:border-white/30'
                } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <HeartCrack 
                  size={14} 
                  className={`sm:w-4 sm:h-4 ${userReactions.includes('dislike') ? 'fill-current' : ''}`} 
                />
                <span>Dislike</span>
                {stats.dislikes_count > 0 && (
                  <span className="bg-black/20 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs">
                    {stats.dislikes_count}
                  </span>
                )}
              </button>
            </div>
          </div>


          {/* Error Message */}
          {error && (
            <div className="mb-4 p-2 bg-red-500/20 border border-red-500/50 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Results - Show after reacting or if there are existing votes */}
          {(showResults || stats.total_votes > 0) && (
            <div className="space-y-2 sm:space-y-3 transition-all duration-500 ease-out">
              {/* Sentiment Summary */}
              <div className="text-center">
                <p className={`text-sm sm:text-lg font-bold ${getSentimentColor()}`}>
                  {getSentimentText()}
                </p>
                <p className="text-white/60 text-xs sm:text-sm">
                  {Math.round(stats.sentiment_score * 100)}% positive • {stats.total_votes} total votes
                </p>
              </div>

              {/* Visual Bar */}
              <div className="relative h-1.5 sm:h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-1000 ease-out"
                  style={{ width: `${Math.round(stats.sentiment_score * 100)}%` }}
                />
                <div
                  className="absolute right-0 top-0 h-full bg-gradient-to-l from-red-500 to-red-400"
                  style={{ width: `${100 - Math.round(stats.sentiment_score * 100)}%` }}
                />
              </div>

              {/* Vote Breakdown - Compact on mobile */}
              {stats.total_votes > 0 && (
                <div className="flex justify-center gap-4 text-xs sm:text-sm">
                  {stats.likes_count > 0 && (
                    <div className="flex items-center gap-1">
                      <Heart size={12} className="text-green-500 sm:w-4 sm:h-4" />
                      <span className="text-white/60">{stats.likes_count} likes</span>
                    </div>
                  )}
                  {stats.dislikes_count > 0 && (
                    <div className="flex items-center gap-1">
                      <HeartCrack size={12} className="text-red-500 sm:w-4 sm:h-4" />
                      <span className="text-white/60">{stats.dislikes_count} dislikes</span>
                    </div>
                  )}
                </div>
              )}

              {/* Total Community Stats */}
              <div className="flex justify-center">
                <div className="flex items-center gap-1 text-white/60 text-xs sm:text-sm">
                  <Users size={12} className="sm:w-[14px] sm:h-[14px]" />
                  <span>{stats.total_votes} total votes</span>
                </div>
              </div>
            </div>
          )}

          {/* Vote Hint */}
          <p className="text-[10px] sm:text-xs text-white/40 text-center mt-2 sm:mt-3">
            {userReactions.length > 0 ? 'Click your vote again to remove it' : 'Share your thoughts on this card!'}
          </p>
        </>
      )}
    </div>
  );
} 